import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as SubmissionsIcon,
  Event as EventsIcon,
  Computer as KioskIcon,
  Settings as SettingsIcon,
  Business as BranchIcon,
  People as UsersIcon,
  SupervisorAccount as SuperAdminIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSuperAdmin, hasPermission } = useAuth();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin/dashboard',
      permission: null // Always available
    },
    {
      text: 'Submissions',
      icon: <SubmissionsIcon />,
      path: '/admin/submissions',
      permission: ['submissions', 'read']
    },
    {
      text: 'Events',
      icon: <EventsIcon />,
      path: '/admin/events',
      permission: ['events', 'read']
    },
    {
      text: 'Kiosk Launcher',
      icon: <KioskIcon />,
      path: '/admin/kiosk',
      permission: ['events', 'read']
    }
  ];

  // SuperAdmin only items
  const superAdminItems = [
    {
      text: 'Branch Management',
      icon: <BranchIcon />,
      path: '/admin/branches',
      permission: null,
      superAdminOnly: true
    },
    {
      text: 'User Management',
      icon: <UsersIcon />,
      path: '/admin/users',
      permission: null,
      superAdminOnly: true
    }
  ];

  // Settings (available to all with appropriate permissions)
  const settingsItems = [
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      permission: ['settings', 'read']
    }
  ];

  const canAccessMenuItem = (item) => {
    if (item.superAdminOnly && !isSuperAdmin()) {
      return false;
    }
    
    if (!item.permission) {
      return true;
    }
    
    return hasPermission(item.permission[0], item.permission[1]);
  };

  const renderMenuItems = (items, showDivider = false) => (
    <>
      {showDivider && <Divider sx={{ my: 1 }} />}
      {items
        .filter(canAccessMenuItem)
        .map((item) => (
          <ListItem
            button
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => navigate(item.path)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
            {item.superAdminOnly && (
              <SuperAdminIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            )}
          </ListItem>
        ))}
    </>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid #ddd'
        }
      }}
    >
      {/* Logo/Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
        <Typography variant="h6" fontWeight="bold" color= "primary">
          BU PlatForm
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Simplify Leads
        </Typography>
        {user?.branch && (
          <Chip
            label={user.branch.displayName}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ overflow: 'auto', py: 1 }}>
        <List dense>
          {renderMenuItems(menuItems)}
          {isSuperAdmin() && renderMenuItems(superAdminItems, true)}
          {renderMenuItems(settingsItems, true)}
        </List>
      </Box>

      {/* Footer */}
      
    </Drawer>
  );
};

export default Sidebar;